'use client'

import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'
import 'swiper/css'
import 'swiper/css/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigation } from 'swiper/modules'
import { ChevronLeft, ChevronRight, Grid as GridIcon, Camera as CameraIcon } from 'lucide-react'
import Image from 'next/image'
import type { GalleryResult } from '../data/GalleryFragment'
import clsx from 'clsx'
import styles from './GalleryCarousel.module.sass'
import { SwiperSlide } from 'swiper/react'
import { Swiper } from 'swiper/react'

export type GalleryCarouselProps = {
	/** Optional heading shown above the gallery */
	title?: string
	/** Collection of images coming from Contember CMS */
	items: GalleryResult['items']
	/** If the gallery is rendered inside rich-content we tweak paddings a bit */
	isContent?: boolean
	/** When gallery is placed on dark background we lighten texts */
	isDarkBg?: boolean
}

export const GalleryCarousel = ({ title, items, isContent = false, isDarkBg = false }: GalleryCarouselProps) => {
	// View state (grid vs carousel). We default to carousel.

	// Swiper instance references & helpers
	// biome-ignore lint/suspicious/noExplicitAny: Swiper typings are pulled in run-time – explicit type is not important here
	const swiperRef = useRef<any>(null)
	const swiperWrapperRef = useRef<HTMLDivElement | null>(null)

	const [showLeftArrow, setShowLeftArrow] = useState(false)
	const [showRightArrow, setShowRightArrow] = useState(false)
	const [showArrows, setShowArrows] = useState(false)

	/** Initialise PhotoSwipe lightbox for the current layout (grid / carousel) */
	useEffect(() => {
		const gallerySelector = '.gallery-swiper .swiper-wrapper'

		const lightbox = new PhotoSwipeLightbox({
			gallery: gallerySelector,
			children: 'a',
			pswpModule: () => import('photoswipe'),
		})

		// Provide our own caption renderer so we can reuse the hidden DOM markup
		lightbox.on('uiRegister', () => {
			// @ts-expect-error – PhotoSwipe's UI typings are incomplete
			lightbox?.pswp?.ui.registerElement({
				name: 'custom-caption',
				order: 9,
				isButton: false,
				appendTo: 'root',
				html: '',
				// biome-ignore lint/suspicious/noExplicitAny: PhotoSwipe public API does not ship typings for the pswp instance
				onInit: (el: HTMLElement, pswp: any) => {
					pswp.on('change', () => {
						const currSlideElement = pswp.currSlide?.data.element as HTMLElement | undefined
						let captionHTML = ''

						if (currSlideElement) {
							const hiddenCaption = currSlideElement.querySelector<HTMLElement>('.hidden-caption-content')
							if (hiddenCaption) {
								captionHTML = hiddenCaption.innerHTML
							} else {
								captionHTML = currSlideElement.querySelector('img')?.getAttribute('alt') ?? ''
							}
						}
						el.innerHTML = captionHTML || ''
					})
				},
			})
		})

		lightbox.init()
		return () => {
			lightbox.destroy()
		}
	}, [])

	/** Swiper arrow visibility helpers */
	const updateArrowVisibility = useCallback(() => {
		if (swiperRef.current && swiperWrapperRef.current) {
			const swiperWidth = swiperWrapperRef.current.offsetWidth
			const slides = swiperRef.current.slides as HTMLElement[]
			let totalSlidesWidth = 0

			for (const slide of slides) {
				totalSlidesWidth += slide.offsetWidth
			}

			const spaceBetween = swiperRef.current.params.spaceBetween as number
			totalSlidesWidth += (slides.length - 1) * spaceBetween

			if (totalSlidesWidth > swiperWidth) {
				setShowArrows(true)
				setShowRightArrow(!swiperRef.current.isEnd)
			} else {
				setShowArrows(false)
			}
		}
	}, [])

	const handleSlideChange = () => {
		if (swiperRef.current) {
			setShowLeftArrow(!swiperRef.current.isBeginning)
			setShowRightArrow(!swiperRef.current.isEnd)
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: Swiper typings are pulled in run-time – explicit type is not important here
	const handleSwiperInit = (swiper: any) => {
		swiperRef.current = swiper
		handleSlideChange()
		updateArrowVisibility()
	}

	if (!items?.length) return null

	return (
		<section>
			<div className={clsx('z-20 flex flex-col mb-10', !isContent && 'md:px-4 xl:px-[2.5rem]')}>
				<div className="block">
					<div
						className={clsx(
							'flex gap-2 items-center',
							title ? 'justify-between' : 'justify-end',
							isContent ? 'mt-6 mb-4 md:mb-8' : 'mt-20 mb-8 md:mb-16',
						)}
					>
						{title && (
							<h1
								className={clsx(
									isContent
										? 'md:text-2xl !mb-0 h-fit xl:text-4xl font-bold max-w-[1115px]'
										: 'text-2xl md:text-5xl max-w-screen font-bold flex md:gap-2 [&_svg]:max-md:w-5',
									isDarkBg ? 'text-white' : 'text-black',
								)}
							>
								{title}
							</h1>
						)}
					</div>
				</div>

				<div ref={swiperWrapperRef} className="relative">
					<Swiper
						onSwiper={handleSwiperInit}
						onSlideChange={handleSlideChange}
						modules={[Navigation]}
						slidesPerView="auto"
						spaceBetween={20}
						className="swiper-container gallery-swiper"
					>
						{items.map((item) => (
							<SwiperSlide
								key={item.image?.url}
								style={{ width: 'auto', marginRight: '20px' }}
								className="flex-shrink-0"
							>
								<a
									href={item.image?.url}
									data-pswp-width={item.image?.width ?? 400}
									data-pswp-height={item.image?.height ?? 400}
									className="block"
								>
									<Image
										quality={85}
										width={550}
										height={400}
										src={item.image?.url ?? ''}
										alt={item.image?.alt ?? ''}
										className="object-cover object-center rounded-3xl h-[250px] md:h-[400px] max-w-[280px] md:max-w-[550px] w-fit"
									/>

									<span className="hidden-caption-content" style={{ display: 'none' }}>
										<span className="caption-alt">{item.image?.alt ?? ''}</span>
									</span>
								</a>
							</SwiperSlide>
						))}
					</Swiper>

					{showArrows && (
						<>
							{showLeftArrow && (
								<button
									type="button"
									onClick={() => swiperRef.current?.slidePrev()}
									className={styles.left}
								>
									<ChevronLeft size={24} />
								</button>
							)}

							{showRightArrow && (
								<button
									type="button"
									onClick={() => swiperRef.current?.slideNext()}
									className={styles.right}
								>
									<ChevronRight size={24} />
								</button>
							)}
						</>
					)}
				</div>
			</div>
		</section>
	)
}
